import { ENV } from "../env";
/**
 * پرونده‌ی سلامت (آزمایشی) — همه‌ی درخواست‌های بک‌اند همین‌جاست.
 * بک‌اند وقتی قابلیت برای کاربر خاموش است ۴۰۴ می‌دهد، پس هر خطا یعنی «چیزی نشان نده».
 * پاسخ موفق: {status:'OK', data} — خطا: {status:'ERROR', code, message}
 *
 * حالت نمایشی (فقط در اجرای محلی): ?hrmock=1 — در نسخه‌ی ساخته‌شده ENV.DEV
 * ثابت false است و این شاخه و فایل داده‌ی نمایشی کلاً از بسته حذف می‌شوند.
 */

import { apiFetch, getApiUrl } from "./client";

export const HEALTH_RECORD_KINDS = ["lab", "imaging", "prescription", "body_photo"];

export const KIND_LABEL = {
  lab: "آزمایش",
  imaging: "تصویربرداری",
  prescription: "نسخه",
  body_photo: "عکس",
};

export const FOLDER_LABEL = {
  lab: "آزمایش‌ها",
  imaging: "تصویربرداری",
  prescription: "نسخه‌ها",
  body_photo: "عکس‌ها",
};

function mockEnabled() {
  if (!ENV.DEV) return false;
  try {
    return new URLSearchParams(window.location.search).has("hrmock");
  } catch {
    return false;
  }
}

async function mockCall(name, ...args) {
  if (!ENV.DEV) throw new Error("unavailable");
  const mock = await import("../components/healthRecord/healthRecordMock.js");
  return mock[name](...args);
}

async function request(path, options = {}, fallbackMessage = "انجام نشد.") {
  const res = await apiFetch(path, options);
  const body = await res.json().catch(() => ({}));
  if (!res.ok || String(body?.status ?? "").toUpperCase() === "ERROR") {
    const err = new Error(body?.message || fallbackMessage);
    err.status = res.status;
    err.code = body?.code ?? null;
    throw err;
  }
  return body?.data ?? {};
}

/* ---------- شکل داده ---------- */

export function attachmentDownloadUrl(attachmentId) {
  if (attachmentId == null) return null;
  return getApiUrl(`/attachments/${attachmentId}/download`);
}

function absoluteApiUrl(path) {
  return /^(https?:|data:|blob:)/.test(path) ? path : getApiUrl(path);
}

function normalizeItem(raw) {
  if (!raw || raw.id == null) return null;
  const attachmentId = raw.attachment_id ?? null;
  const downloadPath = raw.download_url || (attachmentId != null ? `/attachments/${attachmentId}/download` : null);
  const url = downloadPath ? absoluteApiUrl(downloadPath) : null;
  return {
    id: Number(raw.id),
    kind: raw.kind,
    recordDate: raw.record_date ?? raw.created_at ?? null,
    doctorName: raw.doctor_name || null,
    attachmentId: attachmentId != null ? Number(attachmentId) : null,
    thumbnailUrl: raw.thumbnail_url ? absoluteApiUrl(raw.thumbnail_url) : url,
    url,
    subjectLabel: raw.subject_label || null,
    ticketId: raw.source_ticket_id ?? null,
    senderRole: raw.sender_role === "doctor" ? "doctor" : "owner",
    mimetype: raw.mimetype || null,
  };
}

function byNewest(a, b) {
  return new Date(b.recordDate || 0) - new Date(a.recordDate || 0);
}

function normalizeFolders(raw) {
  const out = {};
  for (const kind of HEALTH_RECORD_KINDS) {
    const list = Array.isArray(raw?.[kind]) ? raw[kind] : [];
    out[kind] = list.map(normalizeItem).filter(Boolean).sort(byNewest);
  }
  return out;
}

function normalizeSummary(raw) {
  if (!raw) return null;
  let summary = raw.summary ?? raw.summary_json ?? null;
  if (typeof summary === "string") {
    try {
      summary = JSON.parse(summary);
    } catch {
      summary = { chief_complaint: summary };
    }
  }
  return {
    id: raw.id ?? raw.ticket_id,
    ticketId: raw.ticket_id ?? null,
    date: raw.date ?? raw.created_at ?? null,
    doctorName: raw.doctor_name || null,
    memberLabel: raw.member_label || null,
    editedByDoctor: Boolean(raw.edited_by_doctor),
    isTherapy: Boolean(raw.is_therapy || summary?.is_therapy),
    summary: summary && typeof summary === "object" ? summary : {},
  };
}

function normalizePrescriptions(list) {
  return (Array.isArray(list) ? list : [])
    .filter((p) => p && String(p.drug || "").trim())
    .map((p, i) => ({
      key: `${p.ticket_id ?? "x"}-${i}`,
      drug: String(p.drug).trim(),
      date: p.date ?? null,
      doctorName: p.doctor_name || null,
      ticketId: p.ticket_id ?? null,
      memberLabel: p.member_label || null,
    }))
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

/** خلاصه‌ی غیربالینی (هماهنگی، نوبت) کارت نمی‌شود. بک‌اند هم نمی‌فرستد، این فقط پشتیبان است. */
export function isClinicalSummary(entry) {
  const s = entry?.summary;
  if (!s || typeof s !== "object") return false;
  if (s.is_clinical === false) return false;
  return true;
}

/** یک کارت برای هر گفت‌وگو: فقط تازه‌ترین خلاصه‌ی بالینیِ همان گفت‌وگو. */
export function normalizeSummaries(list) {
  const sorted = (Array.isArray(list) ? list : [])
    .map(normalizeSummary)
    .filter(Boolean)
    .filter(isClinicalSummary)
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0) || Number(b.id) - Number(a.id));
  const seen = new Set();
  return sorted.filter((e) => {
    if (e.ticketId == null) return true;
    const key = String(e.ticketId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeGrant(raw) {
  if (!raw || raw.id == null) return null;
  return {
    id: Number(raw.id),
    granteeName: raw.grantee_name || null,
    status: raw.status,
    ticketId: raw.ticket_id ?? null,
    lastViewedAt: raw.last_viewed_at ?? null,
    scope: normalizeScope(raw.scope),
  };
}

/** چیزی که پزشک می‌بیند. پیش‌فرض: همه‌چیز جز جلسه‌های روان‌درمانی. */
export const DEFAULT_SCOPE = Object.freeze({ visits: true, meds: true, measurements: true, files: true, therapy: false, hidden_visit_ids: [], hidden_item_ids: [] });

/** ویزیت‌های پرونده برای «ویزیت‌های مشخص را پنهان کن». */
export function visitChoices(summaries) {
  return (summaries || [])
    .filter((s) => s.ticketId != null)
    .map((s) => ({ ticketId: Number(s.ticketId), title: s.summary?.chief_complaint || "ویزیت", date: s.date, doctorName: s.doctorName }));
}

export function normalizeScope(raw) {
  const out = { ...DEFAULT_SCOPE, hidden_visit_ids: [], hidden_item_ids: [] };
  if (!raw || typeof raw !== "object") return out;
  for (const k of ["visits", "meds", "measurements", "files", "therapy"]) if (typeof raw[k] === "boolean") out[k] = raw[k];
  if (Array.isArray(raw.hidden_visit_ids)) out.hidden_visit_ids = raw.hidden_visit_ids.map(Number).filter((n) => Number.isInteger(n) && n > 0);
  // موارد مشخص (نشانی کوتاه، مثل file:12). ویزیت‌ها همچنان در hidden_visit_ids هم می‌مانند.
  if (Array.isArray(raw.hidden_item_ids)) out.hidden_item_ids = [...new Set(raw.hidden_item_ids.filter((x) => typeof x === "string" && x.includes(":")))];
  return out;
}

function normalizeRecord(data) {
  const members = Array.isArray(data?.members)
    ? data.members.map((m) => m?.label).filter(Boolean)
    : Array.isArray(data?.family_labels)
      ? data.family_labels.filter(Boolean)
      : [];
  return {
    members,
    folders: normalizeFolders(data?.folders),
    summaries: normalizeSummaries(data?.summaries),
    prescriptions: normalizePrescriptions(data?.prescriptions),
    grants: (Array.isArray(data?.grants) ? data.grants : []).map(normalizeGrant).filter(Boolean),
  };
}

/* ---------- قابلیت ---------- */

/** فقط true وقتی سرور صریحاً گفته روشن است. هر خطا یعنی خاموش. */
export async function fetchHealthRecordFeature() {
  if (mockEnabled()) return true;
  try {
    const data = await request("/features");
    return data?.healthRecord === true;
  } catch {
    return false;
  }
}

/* ---------- بیمار ---------- */

export async function fetchHealthRecord() {
  if (mockEnabled()) return normalizeRecord(await mockCall("getRecord"));
  return normalizeRecord(await request("/health-record"));
}

export async function moveHealthRecordItem(itemId, kind) {
  if (mockEnabled()) return mockCall("patchItem", itemId, { kind });
  return request(`/health-record/items/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify({ kind }),
  });
}

export async function removeHealthRecordItem(itemId) {
  if (mockEnabled()) return mockCall("patchItem", itemId, { remove: true });
  // بک‌اند `remove` را می‌خواند؛ `removed` هم برای سازگاری با قرارداد اولیه فرستاده می‌شود.
  return request(`/health-record/items/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify({ remove: true, removed: true }),
  });
}

export async function revokeHealthRecordGrant(grantId) {
  if (mockEnabled()) return mockCall("revokeGrant", grantId);
  return request(`/health-record/grants/${grantId}`, { method: "DELETE" });
}

/** scope اختیاری است: وقتی بیمار «فقط بخشی را نشان بده» را انتخاب کرده. */
export async function decideHealthRecordGrant(grantId, decision, scope) {
  if (mockEnabled()) return mockCall("decideGrant", grantId, decision, scope);
  return request(`/health-record/grants/${grantId}/decide`, {
    method: "POST",
    body: JSON.stringify(scope ? { decision, scope } : { decision }),
  });
}

export async function setHealthRecordGrantScope(grantId, scope) {
  if (mockEnabled()) return mockCall("setGrantScope", grantId, scope);
  return request(`/health-record/grants/${grantId}/scope`, {
    method: "PATCH",
    body: JSON.stringify({ scope }),
  });
}

/* ---------- پزشک ---------- */

/**
 * وضعیت پرونده برای پزشکِ این گفت‌وگو.
 * @returns {Promise<null | {status, itemCount, summaries, folders}>} null یعنی هیچ چیزی نشان نده.
 */
export async function fetchDoctorHealthRecord(ticketId) {
  if (mockEnabled()) return shapeDoctorView(await mockCall("doctorView", ticketId));
  try {
    return shapeDoctorView(await request(`/tickets/${ticketId}/health-record`));
  } catch (err) {
    // بک‌اند تازه وضعیت را با ۲۰۰ می‌دهد. ۴۰۳ فقط برای سازگاری با نسخه‌ی قدیمی‌تر است.
    if (err.status === 403) return { status: "none", itemCount: 0, summaries: [], folders: normalizeFolders({}) };
    return null;
  }
}

function shapeDoctorView(data) {
  const folders = normalizeFolders(data?.folders);
  const count = HEALTH_RECORD_KINDS.reduce((n, k) => n + folders[k].length, 0);
  return {
    status: data?.status || "granted",
    itemCount: data?.item_count ?? count,
    summaries: normalizeSummaries(data?.summaries),
    folders,
    patientEntries: Array.isArray(data?.patient_entries) ? data.patient_entries : [],
    patient: data?.patient && typeof data.patient === "object" ? data.patient : null,
    patientBrief: typeof data?.patient_brief === "string" ? data.patient_brief.trim() : "",
    partial: data?.partial === true,
    viewerType: data?.viewer_type === "psychologist" ? "psychologist" : "doctor",
    sessions: normalizeSummaries(data?.sessions),
    sessionNumber: Number(data?.session_number) || null,
    risk: data?.risk && typeof data.risk === "object" ? data.risk : null,
    prescriptions: normalizePrescriptions(data?.prescriptions),
  };
}

export async function requestHealthRecordAccess(ticketId) {
  if (mockEnabled()) return mockCall("requestAccess", ticketId);
  const data = await request(`/tickets/${ticketId}/health-record/request`, { method: "POST" });
  return { status: data?.grant?.status || "requested" };
}

/* ---------- پیام سیستمی ---------- */

/** علامتی که سرور روی پیام می‌گذارد: { type: "access_request" | "granted", grant_id, status }. */
export function healthRecordMarker(msg) {
  const m = msg?.health_record ?? msg?.healthRecord ?? null;
  return m && typeof m === "object" ? m : null;
}

export function isHealthRecordAccessMessage(msg) {
  if (healthRecordMarker(msg)?.type === "access_request") return true;
  const kind = String(msg?.auto_reply_kind ?? msg?.autoReplyKind ?? msg?.kind ?? "")
    .trim()
    .toLowerCase();
  return kind === "health_record_access";
}

export function grantIdFromMessage(msg) {
  const raw =
    healthRecordMarker(msg)?.grant_id ??
    msg?.grant_id ??
    msg?.grantId ??
    msg?.metadata?.grant_id ??
    msg?.metadata?.grantId ??
    msg?.meta?.grant_id ??
    null;
  return raw != null ? Number(raw) : null;
}
